import {
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
} from '@mui/material';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { useLanguage } from '../../../contexts/language';
import useForm from '../../../hooks/useForm';
import getMaterialInputsFormHelper from '../../../utils/getMaterialInputsFormHelper';
import { JourneyEditDialogState } from './types';

const JourneyEditDialog = ({
  dialogState,
  loading,
  onClose,
  onConfirm,
}: {
  dialogState: JourneyEditDialogState;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
}) => {
  const { t } = useLanguage();

  const formState = useForm({
    initialValues: { note: '', confirmCheckbox: false },
    onSubmit: formValues => {
      onConfirm(formValues.note);
      formState.reset();
    },
    validate: ({ note, confirmCheckbox }, setError) => {
      if (!note.trim()) {
        setError('note', t('validation.required-field'));
      }
      if (!confirmCheckbox) {
        setError('confirmCheckbox', t('validation.required-checkbox'));
      }
    },
  });

  const { getTextFieldProps, getCheckboxProps, renderError } =
    getMaterialInputsFormHelper(formState);

  const handleClose = () => {
    onClose();
    formState.reset();
  };

  return (
    <ConfirmationDialog
      open={Boolean(dialogState)}
      onClose={handleClose}
      onConfirm={formState.handleSubmit}
      loading={loading}
      title={
        dialogState
          ? t('content.field-changed', {
              FIELD: t(dialogState.changeReason.titleTranslationKey),
            })
          : ''
      }
      submitDisabled={formState.hasVisibleErrors}
    >
      <p className="mb-4 text-gray-500">
        {t(`content.edit-journey-reason-note`)}
      </p>
      <TextField
        {...getTextFieldProps('note', true)}
        placeholder={`${t('content.start-typing-here')}...`}
        multiline
        rows={5}
      />
      <FormControl
        error={getCheckboxProps('confirmCheckbox').error}
        component="fieldset"
        variant="standard"
        className="max-md: mt-2"
      >
        <FormControlLabel
          control={<Checkbox {...getCheckboxProps('confirmCheckbox')} />}
          sx={{
            color: getCheckboxProps('confirmCheckbox').error
              ? 'error.main'
              : undefined,
            '& .MuiFormControlLabel-label': {
              fontSize: {
                xs: '0.875rem',
                md: '1rem',
              },
            },
          }}
          label={t('content.confirmation-checkbox-publicly-visible-disclaimer')}
        />
        {renderError('confirmCheckbox')}
      </FormControl>
    </ConfirmationDialog>
  );
};

export default JourneyEditDialog;
